import type {
  Account,
  GcpBigQueryResourceTablesInner,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { dayjs } from '@/utils/dayjs';

import { BigQueryConfiguration } from './steps/bigQueryConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreBigQueryState {
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  targetDatasetId: string;
  targetLocation: string;
  availableTables: GcpBigQueryResourceTablesInner[];
  selectedTables: string[];
}

const restoreBigQueryFlow: Step<RestoreBigQueryState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'big_query_configuration',
      Component: BigQueryConfiguration,
    },
  ],
};

const formatDateTimeForDatasetId = (): string => {
  return dayjs().utc().format('YYYYMMDDHHmmss');
};

export const RestoreBigQueryWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const bigQueryProps = resource.resourceProperties?.gcpBigQuery;
  const originalDatasetId = bigQueryProps?.datasetId || '';
  const originalLocation = bigQueryProps?.location || '';
  const availableTables = bigQueryProps?.tables || [];
  const allTableNames = availableTables
    .map((t) => t.name || '')
    .filter(Boolean);

  const defaultTargetDatasetId = originalDatasetId
    ? `${originalDatasetId}_${formatDateTimeForDatasetId()}`
    : '';

  return (
    <Wizard<RestoreBigQueryState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreBigQueryFlow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders: [Provider.Gcp],
        targetDatasetId: defaultTargetDatasetId,
        targetLocation: originalLocation,
        availableTables,
        selectedTables: allTableNames,
      }}
      title='Restore BigQuery Dataset'
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
